import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Drawer,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import BadgeIcon from '@mui/icons-material/Badge';
import {
  BOOKING_DRAWER_ACCENT,
  getBookingDrawerFooterSx,
  getBookingDrawerInputBg,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerTheme,
} from '../../../utils/bookingDrawerLayout';
import { getDrawerRootSx } from '../../../utils/drawerOverlayProps';
import { formatDocumentType } from './grcUtils';
import { getHotelBrandingFromContext, loadHotelBranding } from './hotelBranding';
import { buildGuestRegistrationCardData } from './buildGuestRegistrationCardData';
import { printGuestDocuments } from './printGuestDocuments';
import { downloadGuestRegistrationCardPdf, printGuestRegistrationCard } from './printGuestRegistrationCard';
import GuestRegistrationCard from './GuestRegistrationCard';

const ACCENT = BOOKING_DRAWER_ACCENT || '#4ce1b6';

export default function GuestDocumentsDrawer({
  open,
  onClose,
  documents,
  isDark = false,
}) {
  const [activeTab, setActiveTab] = useState('document');
  const [branding, setBranding] = useState(getHotelBrandingFromContext);
  const [downloading, setDownloading] = useState(false);
  const drawerTheme = getBookingDrawerTheme(isDark);
  const showGrc = open && activeTab === 'grc';

  useEffect(() => {
    if (!open) return;
    setActiveTab('document');
    setDownloading(false);
    setBranding(getHotelBrandingFromContext());
  }, [open]);

  useEffect(() => {
    if (!showGrc) return undefined;
    let cancelled = false;
    loadHotelBranding().then((next) => {
      if (!cancelled) setBranding(next);
    });
    return () => {
      cancelled = true;
    };
  }, [showGrc]);

  const grcData = useMemo(
    () => (showGrc ? buildGuestRegistrationCardData({ documents, branding }) : null),
    [showGrc, documents, branding],
  );

  const handlePrint = useCallback(() => {
    if (activeTab === 'grc' && grcData) {
      printGuestRegistrationCard(grcData);
      return;
    }
    printGuestDocuments(documents);
  }, [activeTab, documents, grcData]);

  const handleDownloadPdf = useCallback(async () => {
    if (downloading || !grcData) return;
    setDownloading(true);
    try {
      await downloadGuestRegistrationCardPdf(grcData);
    } finally {
      setDownloading(false);
    }
  }, [downloading, grcData]);

  const hasFront = Boolean(documents?.id_front_url);
  const hasBack = Boolean(documents?.id_back_url);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={getDrawerRootSx()}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          backgroundColor: isDark ? drawerTheme.surface : '#ffffff',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            p: 1.5,
            borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            backgroundColor: isDark ? drawerTheme.headerBg : '#ffffff',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              <DescriptionIcon sx={{ color: ACCENT, fontSize: 24, flexShrink: 0 }} />
              <Typography variant="h6" sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 600 }}>
                Documents
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconButton
                onClick={handlePrint}
                sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
                title={activeTab === 'grc' ? 'Print GRC' : 'Print Documents'}
              >
                <PrintIcon />
              </IconButton>
              <IconButton
                onClick={onClose}
                sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}
                aria-label="Close documents"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {(documents?.bookingCode || documents?.document_type) && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              {documents?.bookingCode && (
                <Chip
                  label={documents.bookingCode}
                  size="small"
                  sx={{
                    backgroundColor: ACCENT,
                    color: '#000',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                  }}
                />
              )}
              {documents?.document_type && (
                <Chip
                  label={formatDocumentType(documents.document_type)}
                  size="small"
                  sx={{
                    backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.15)',
                    color: ACCENT,
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    height: 24,
                  }}
                />
              )}
            </Box>
          )}

          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            sx={{
              mt: 1,
              minHeight: 36,
              '& .MuiTab-root': {
                minHeight: 36,
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.85rem',
              },
              '& .MuiTabs-indicator': { backgroundColor: ACCENT },
            }}
          >
            <Tab value="document" label="Document" icon={<DescriptionIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
            <Tab value="grc" label="GRC" icon={<BadgeIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
          </Tabs>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: activeTab === 'grc' ? 'hidden' : 'auto',
            p: activeTab === 'grc' ? 1 : 1.5,
            display: activeTab === 'grc' ? 'flex' : 'block',
          }}
        >
          {activeTab === 'document' ? (
            <Grid container spacing={2}>
              {hasFront && (
                <Grid item xs={12} md={hasBack ? 6 : 12}>
                  <DocumentImageCard
                    title="ID Front"
                    src={documents.id_front_url}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                  />
                </Grid>
              )}
              {hasBack && (
                <Grid item xs={12} md={hasFront ? 6 : 12}>
                  <DocumentImageCard
                    title="ID Back"
                    src={documents.id_back_url}
                    isDark={isDark}
                    drawerTheme={drawerTheme}
                  />
                </Grid>
              )}
              {!hasFront && !hasBack && (
                <Grid item xs={12}>
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <DescriptionIcon
                      sx={{
                        fontSize: 64,
                        color: isDark ? drawerTheme.subtleText : '#999',
                        mb: 2,
                        opacity: 0.5,
                      }}
                    />
                    <Typography variant="body1" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                      No documents uploaded for this guest
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0, width: '100%' }}>
              {grcData ? <GuestRegistrationCard data={grcData} /> : null}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            p: 1.5,
            display: 'flex',
            gap: 1,
            ...getBookingDrawerFooterSx(isDark),
          }}
        >
          {activeTab === 'grc' && (
            <Button
              variant="contained"
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={handleDownloadPdf}
              disabled={downloading}
              sx={getBookingDrawerPrimaryButtonSx(isDark)}
            >
              {downloading ? 'Preparing PDF…' : 'Download GRC'}
            </Button>
          )}
          <Button
            variant="outlined"
            fullWidth
            onClick={onClose}
            sx={getBookingDrawerOutlinedButtonSx(isDark)}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}

const DocumentImageCard = memo(function DocumentImageCard({ title, src, isDark, drawerTheme }) {
  return (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        borderRadius: 2,
        backgroundColor: isDark ? getBookingDrawerInputBg(isDark) : '#f5f5f5',
        textAlign: 'center',
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ color: isDark ? drawerTheme.text : '#333', mb: 2, fontWeight: 600 }}
      >
        {title}
      </Typography>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: isDark ? '#1a1a1a' : '#e0e0e0',
          cursor: 'pointer',
          minHeight: 200,
          '&:hover .zoom-overlay': { opacity: 1 },
        }}
        onClick={() => src && window.open(src, '_blank')}
      >
        <img
          src={src}
          alt={title}
          style={{ width: '100%', height: 'auto', display: 'block' }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
        <Box
          className="zoom-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0,
            transition: 'opacity 0.2s ease',
            pointerEvents: 'none',
          }}
        >
          <ZoomInIcon sx={{ fontSize: 48, color: '#fff' }} />
        </Box>
      </Box>
    </Paper>
  );
});
